import { ActionIcon, Box, Button, Group, Stack, Text } from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../components/session/postSessionCard';
import { ContentCard } from '../../../components/common/ContentCard';
import { CheckSquare, ChevronDown, ChevronUp, Info } from 'react-feather';
import type { PostSessionParticipant } from '../../../services/api/coachPostSession';
import type { AttendanceDraftMap } from '../../../hooks/useCoachPostSession';
import { useAttendanceSectionCollapse } from '../../../hooks/useAttendanceSectionCollapse';
import ParticipantAttendanceRow, {
  ATTENDANCE_PREFILL_NOTE,
} from '../../coach/components/post-session/ParticipantAttendanceRow';

/**
 * MED-817 / E4.5 — Peer Leader Attendance & Engagement card.
 *
 * Reuses the MED-979 `ParticipantAttendanceRow` (below-row expansion, 5 named
 * engagement levels, reason taxonomy, per-participant Notes) — AC4. Adds two
 * peer-leader-specific behaviors the coach card doesn't have:
 *   - AC5: the leader's own row carries a "You" badge (via `isYou`).
 *   - AC11 / MED-1060 AC5: the section collapses to a summary line ("6 present ·
 *     1 partial · 1 absent") with an expand chevron once attendance is complete
 *     AND saved — not on fields-set alone (that would hide the bottom-right Save
 *     button). Notes are optional and don't count toward completeness.
 */

interface Props {
  participants: PostSessionParticipant[];
  attendance: AttendanceDraftMap;
  leaderUserId: string | null;
  onParticipantChange: (
    userId: string,
    next: AttendanceDraftMap[string]
  ) => void;
  onSave: () => Promise<boolean>;
  saving: boolean;
}

const EMPTY_DRAFT: AttendanceDraftMap[string] = {
  status: null,
  absent_reason: null,
  rating: null,
  coach_notes: null,
  autoPopulated: false,
};

export function PeerLeaderAttendanceCard({
  participants,
  attendance,
  leaderUserId,
  onParticipantChange,
  onSave,
  saving,
}: Props) {
  // MED-1077 — shared collapse behavior (complete + saved → collapsed; never on
  // fields-set alone). Same hook drives the coach Attendance card.
  const { isComplete, summaryLine, collapsed, setCollapsed, markDirty, save } =
    useAttendanceSectionCollapse(participants, attendance, onSave, leaderUserId);

  // MED-1191 AC9 — pre-fill note when the roster arrived pre-populated. Derived
  // from the bundle participants (not the drafts) so it survives local edits and
  // clears only after a save. The leader's own row is never auto-populated, so
  // it never triggers the note on its own (AC5).
  const wasPrepopulated = participants.some(p => p.status_auto_populated);

  const handleParticipantChange = (
    userId: string,
    next: AttendanceDraftMap[string]
  ) => {
    markDirty();
    onParticipantChange(userId, next);
  };

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      header={
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Group gap={8} align="center" wrap="nowrap">
              <CheckSquare size={18} color="#1A1A2E" aria-hidden="true" />
              <Text fz={15} fw={700} c="#1A1A2E">
                Attendance &amp; Engagement
              </Text>
            </Group>
            <Text fz={12} fw={500} c="#9CA3AF">
              {collapsed
                ? summaryLine
                : 'Mark attendance, add reasons for absences, and rate engagement. Medley team will see this.'}
            </Text>
            {/* MED-1191 AC9 — pre-fill source note (only when pre-populated). */}
            {wasPrepopulated && !collapsed && (
              <Group gap={6} align="flex-start" wrap="nowrap">
                <Box style={{ flexShrink: 0, marginTop: 2 }}>
                  <Info size={13} color="#92650E" aria-hidden="true" />
                </Box>
                <Text fz={12} fw={500} c="#92650E">
                  {ATTENDANCE_PREFILL_NOTE}
                </Text>
              </Group>
            )}
          </Stack>
          {isComplete && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setCollapsed(c => !c)}
              aria-label={
                collapsed ? 'Expand attendance' : 'Collapse attendance'
              }
            >
              {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </ActionIcon>
          )}
        </Group>
      }
    >
      {!collapsed && (
        <>
          <Stack gap={0}>
            {participants.map((p, idx) => (
              <Box
                key={p.user_id}
                style={
                  idx < participants.length - 1
                    ? { borderBottom: '1px solid #F3F4F6', paddingBottom: 13 }
                    : undefined
                }
                pt={idx === 0 ? 0 : 12}
              >
                <ParticipantAttendanceRow
                  userId={p.user_id}
                  firstName={p.first_name}
                  lastName={p.last_name}
                  preferredName={p.preferred_name}
                  // MED-1267 (AC22) — peer leaders see the WITHDRAWN treatment too
                  // (AC1: coaches and peer leaders alike).
                  participantStatus={p.participant_status}
                  draft={attendance[p.user_id] ?? EMPTY_DRAFT}
                  onChange={next => handleParticipantChange(p.user_id, next)}
                  isYou={leaderUserId != null && p.user_id === leaderUserId}
                />
              </Box>
            ))}
          </Stack>

          {/* AC2 (matching coach) — Save attendance at the bottom-right. */}
          <Group justify="flex-end">
            <Button
              size="sm"
              onClick={save}
              loading={saving}
              styles={{
                root: {
                  backgroundColor: POST_SESSION_PRIMARY_BG,
                  color: '#FFFFFF',
                },
              }}
            >
              Save Attendance
            </Button>
          </Group>
        </>
      )}
    </ContentCard>
  );
}

export default PeerLeaderAttendanceCard;
